import Model, { attr, belongsTo } from '@ember-data/model';
import { tracked } from '@glimmer/tracking';
import { Type } from '@warp-drive/core-types/symbols';
import type { RiBreakdown } from 'frontend-utils/utils/constants/constant-values';
import { copyableModel } from 'frontend-utils/utils/ember-data';
import type { Rate, RevenueInsightSegmentationData } from 'lumen/models';
import type { RevenueInsightSegmentationDataLike } from 'lumen/models/revenue-insight/segmentation-data';
import type {
  KpiDisplay,
  MappingType,
} from 'lumen/utils/constants/pms-mapping-constants';
import type { KpiCategory } from 'lumen/utils/constants/ri-constant-values';
import CalculatedValues from 'lumen/utils/datamodels/revenue-insight/calculated-values';

export interface Meta {
  has_transient_group_split: boolean;
  is_net_comparison: boolean;
  kpi_categories: KpiCategory[];
  kpi_display: KpiDisplay;
  name: string;
  pms_is_realtime: boolean;
  pms_version: string;
  segment_breakdown: MappingType;
  uses_summary: unknown[];
}

@copyableModel
export default class RevenueInsightSegmentationValue extends Model {
  [Type] = 'revenue-insight/segmentation-value' as const;

  @belongsTo('revenue-insight/segmentation-data', {
    async: false,
    inverse: null,
  })
  declare 'referenceValue.reservations': RevenueInsightSegmentationData;

  @belongsTo('revenue-insight/segmentation-data', {
    async: false,
    inverse: null,
  })
  declare 'value.reservations': RevenueInsightSegmentationData;

  @belongsTo('revenue-insight/segmentation-data', {
    async: false,
    inverse: null,
  })
  declare budgetValue: RevenueInsightSegmentationData;

  @belongsTo('revenue-insight/segmentation-data', {
    async: false,
    inverse: null,
  })
  declare cancellationReferenceValue: RevenueInsightSegmentationData;

  @belongsTo('revenue-insight/segmentation-data', {
    async: false,
    inverse: null,
  })
  declare cancellationReferenceValueFiltered: RevenueInsightSegmentationData;

  @belongsTo('revenue-insight/segmentation-data', {
    async: false,
    inverse: null,
  })
  declare cancellationValue: RevenueInsightSegmentationData;

  @belongsTo('revenue-insight/segmentation-data', {
    async: false,
    inverse: null,
  })
  declare cancellationValueFiltered: RevenueInsightSegmentationData;

  @belongsTo('revenue-insight/segmentation-data', {
    async: false,
    inverse: null,
  })
  declare forecastReference: RevenueInsightSegmentationData;

  @belongsTo('revenue-insight/segmentation-data', {
    async: false,
    inverse: null,
  })
  declare forecastValue: RevenueInsightSegmentationData;

  @belongsTo('revenue-insight/segmentation-data', {
    async: false,
    inverse: null,
  })
  declare referenceValue: RevenueInsightSegmentationData;

  @belongsTo('revenue-insight/segmentation-data', {
    async: false,
    inverse: null,
  })
  declare value: RevenueInsightSegmentationData;

  @attr declare meta: Meta;
  @attr declare parentId: string | null;
  @attr declare segmentationId: RiBreakdown;
  @attr declare segmentationValue: string;
  @attr declare segmentationValueId: string;
  @attr declare segmentationValueType: unknown;
  @attr declare unfilteredId: string;

  @tracked children?: SegmentationValueLike[];
  @tracked day?: unknown;
  @tracked displayValue?: string;
  @tracked isAggregatedRow?: boolean;
  @tracked isDisabled?: boolean;
  @tracked isDummy?: boolean;
  @tracked isSelectable?: boolean;
  @tracked parent?: SegmentationValueLike;
  @tracked pickupCount?: unknown;
  @tracked rates?: Record<string, Rate[]>;
  @tracked segmentationSortValue?: unknown;
  @tracked totalCapacity?: number;
  @tracked unfiltered_data?: unknown;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get calculatedValues() {
    return new CalculatedValues(this);
  }
}

type SegmentationValueWithDataLike = {
  [K in keyof RevenueInsightSegmentationValue]: RevenueInsightSegmentationValue[K] extends RevenueInsightSegmentationData
    ? RevenueInsightSegmentationDataLike
    : RevenueInsightSegmentationValue[K];
};

export type SegmentationValueLike = Partial<
  Omit<SegmentationValueWithDataLike, keyof Model>
>;
